import React, { useState, useEffect, useMemo } from "react";
import PerformanceMetrics from "./PerformanceMetrics";
import "../styles/DispatchDashboard.css";

const DispatchDashboard = ({ tasks, drivers, onRefresh, dateRange, isOpen, onClose }) => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showPerformanceMetrics, setShowPerformanceMetrics] = useState(false);

  // Calculate comprehensive metrics with real data
  const metrics = useMemo(() => {
    try {
      console.log('DispatchDashboard: Calculating metrics...', { tasks, drivers });
      
      const allTasks = Object.values(tasks || {}).flat().filter(Boolean);
      const totalOrders = allTasks.length;
      const totalDrivers = Object.values(drivers || {}).flat().filter(Boolean).length;
      
      console.log('DispatchDashboard: Total orders:', totalOrders, 'Total drivers:', totalDrivers);
    
    // Current time for calculations
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Filter tasks for today with safe access
    const todayTasks = allTasks.filter(task => {
      if (!task || !task.created_at) return false;
      try {
        const taskDate = new Date(task.created_at);
        if (isNaN(taskDate.getTime())) return false;
        return taskDate >= todayStart;
      } catch (error) {
        console.warn('DispatchDashboard: Error parsing task date:', error);
        return false;
      }
    });

    // Urgent orders (older than 15 minutes) with safe access
    const urgentOrders = (tasks.nonaccepter || []).filter(task => {
      if (!task || !task.created_at) return false;
      try {
        const createdAt = new Date(task.created_at);
        if (isNaN(createdAt.getTime())) return false;
        const diffMinutes = (now - createdAt) / (1000 * 60);
        return diffMinutes > 15;
      } catch (error) {
        console.warn('DispatchDashboard: Error calculating urgent order time:', error);
        return false;
      }
    }).length;

    // Driver statistics with safe access
    const freeDrivers = drivers.free?.length || 0;
    const busyDrivers = drivers.busy?.length || 0;
    const inactiveDrivers = drivers.inactive?.length || 0;
    const externalDrivers = drivers.externe?.length || 0;

    // Task statistics with safe access
    const pendingAssignments = tasks.unassigned?.length || 0;
    const readyOrders = tasks.prete?.length || 0;
    const completedToday = (tasks.completed || []).filter(task => {
      if (!task || !task.created_at) return false;
      const taskDate = new Date(task.created_at);
      return taskDate >= todayStart;
    }).length;

    // Failed orders today
    const failedToday = todayTasks.filter(task => 
      task && task.status_delivery === 'FAILED'
    ).length;

    // Calculate revenue for today
    const todayRevenue = todayTasks.reduce((sum, task) => {
      return sum + (parseFloat(task?.prix_total) || 0);
    }, 0);

    // Calculate average delivery time
    const completedWithTimes = (tasks.completed || []).filter(task => 
      task && task.created_at && task.updated_at && task.status_delivery === 'SUCCESS'
    );
    const avgDeliveryTime = completedWithTimes.length > 0 
      ? completedWithTimes.reduce((sum, task) => {
          const created = new Date(task.created_at);
          const updated = new Date(task.updated_at);
          return sum + (updated - created) / (1000 * 60);
        }, 0) / completedWithTimes.length
      : 0;

    // Success rate calculation
    const successRate = todayTasks.length > 0 
      ? (completedToday / todayTasks.length) * 100 
      : 0;

    // Driver utilization
    const activeDrivers = freeDrivers + busyDrivers;
    const utilizationRate = totalDrivers > 0 ? (activeDrivers / totalDrivers) * 100 : 0;

    // Peak hour calculation (last 24 hours)
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const recentTasks = allTasks.filter(task => {
      const taskDate = new Date(task.created_at);
      return taskDate >= last24Hours;
    });

    const hourStats = {};
    recentTasks.forEach(task => {
      const hour = new Date(task.created_at).getHours();
      hourStats[hour] = (hourStats[hour] || 0) + 1;
    });

    const peakHour = Object.entries(hourStats)
      .sort(([,a], [,b]) => b - a)[0];

    // Average order value
    const avgOrderValue = todayTasks.length > 0 
      ? todayRevenue / todayTasks.length 
      : 0;

    // Longest wait time with safe access
    const longestWait = tasks.unassigned && tasks.unassigned.length > 0 
      ? Math.max(...tasks.unassigned.map(task => {
          if (!task || !task.created_at) return 0;
          try {
            const createdAt = new Date(task.created_at);
            if (isNaN(createdAt.getTime())) return 0;
            return Math.floor((now - createdAt) / (1000 * 60));
          } catch (error) {
            console.warn('DispatchDashboard: Error calculating wait time:', error);
            return 0;
          }
        }), 0)
      : 0;

    // Driver performance (orders per driver)
    const driverOrders = {};
    todayTasks.forEach(task => {
      if (task.livreur_affecte && task.livreur_affecte !== 'Not Assigned') {
        driverOrders[task.livreur_affecte] = (driverOrders[task.livreur_affecte] || 0) + 1;
      }
    });

    const avgOrdersPerDriver = Object.keys(driverOrders).length > 0
      ? Object.values(driverOrders).reduce((sum, count) => sum + count, 0) / Object.keys(driverOrders).length
      : 0;

      // Helper function to hide error values
      const hideErrorValue = (value, minValidValue = 0) => {
        if (value === 1 && totalOrders < 2) return 0; // Hide "1" if no real data
        if (value < minValidValue) return 0;
        return value;
      };

      const result = {
        totalOrders,
        totalDrivers,
        urgentOrders: hideErrorValue(urgentOrders),
        freeDrivers: hideErrorValue(freeDrivers),
        busyDrivers: hideErrorValue(busyDrivers),
        inactiveDrivers: hideErrorValue(inactiveDrivers),
        externalDrivers: hideErrorValue(externalDrivers),
        pendingAssignments: hideErrorValue(pendingAssignments),
        readyOrders: hideErrorValue(readyOrders),
        completedToday: hideErrorValue(completedToday),
        failedToday: hideErrorValue(failedToday),
        todayRevenue: hideErrorValue(todayRevenue),
        avgDeliveryTime: hideErrorValue(Math.round(avgDeliveryTime || 0)),
        utilizationRate: hideErrorValue(Math.round(utilizationRate || 0)),
        successRate: hideErrorValue(Math.round(successRate || 0)),
        peakHour: peakHour ? `${peakHour[0]}:00` : 'N/A',
        avgOrderValue: hideErrorValue(Math.round(avgOrderValue || 0)),
        longestWait: hideErrorValue(longestWait),
        avgOrdersPerDriver: hideErrorValue(Math.round((avgOrdersPerDriver || 0) * 10) / 10),
        todayTasksCount: hideErrorValue(todayTasks.length),
        activeDrivers: hideErrorValue(activeDrivers)
      };
      
      console.log('DispatchDashboard: Calculated metrics:', result);
      return result;
      
    } catch (error) {
      console.error('DispatchDashboard: Error calculating metrics:', error);
      return {
        totalOrders: 0,
        totalDrivers: 0,
        urgentOrders: 0,
        freeDrivers: 0,
        busyDrivers: 0,
        inactiveDrivers: 0,
        externalDrivers: 0,
        pendingAssignments: 0,
        readyOrders: 0,
        completedToday: 0,
        failedToday: 0,
        todayRevenue: 0,
        avgDeliveryTime: 0,
        utilizationRate: 0,
        successRate: 0,
        peakHour: 'N/A',
        avgOrderValue: 0,
        longestWait: 0,
        avgOrdersPerDriver: 0,
        todayTasksCount: 0,
        activeDrivers: 0
      };
    }
  }, [tasks, drivers]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'urgent': return '#ff4757';
      case 'warning': return '#ffa502';
      case 'success': return '#2ed573';
      case 'info': return '#3742fa';
      default: return '#57606f';
    }
  };

  const getDriverStatusColor = (status) => {
    switch (status) {
      case 'FREE': return '#2ed573';
      case 'BUSY': return '#ffa502';
      case 'INACTIVE': return '#ff4757';
      case 'EXTERNE': return '#3742fa';
      default: return '#57606f';
    }
  };

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        onRefresh();
      }, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, onRefresh]);

  // View urgent orders functionality
  const handleViewUrgentOrders = () => {
    // Filter urgent orders (older than 15 minutes)
    const urgentOrders = tasks.nonaccepter?.filter(task => {
      if (!task || !task.created_at) return false;
      const now = new Date();
      const createdAt = new Date(task.created_at);
      const diffMinutes = (now - createdAt) / (1000 * 60);
      return diffMinutes > 15;
    }) || [];

    if (urgentOrders.length === 0) {
      alert('Aucune commande urgente pour le moment !');
      return;
    }

    // Create a detailed message about urgent orders
    let message = `🚨 ALERTE COMMANDES URGENTES 🚨\n\n`;
    message += `Trouvé ${urgentOrders.length} commande(s) urgente(s) :\n\n`;
    
    urgentOrders.forEach((order, index) => {
      const waitTime = Math.floor((new Date() - new Date(order.created_at)) / (1000 * 60));
      message += `${index + 1}. Commande #${order.id}\n`;
      message += `   ⏰ En attente: ${waitTime} minutes\n`;
      message += `   📍 De: ${order.restaurant_name || 'Inconnu'}\n`;
      message += `   💰 Valeur: ${order.prix_total || '0'} MAD\n\n`;
    });

    message += `Action requise: Veuillez assigner des livreurs immédiatement !`;
    alert(message);
  };

  // Export data functionality
  const handleExportData = () => {
    try {
      // Create comprehensive data object
      const exportData = {
        timestamp: new Date().toISOString(),
        metrics: {
          totalOrders: metrics.totalOrders,
          todayOrders: metrics.todayTasksCount,
          completedToday: metrics.completedToday,
          failedToday: metrics.failedToday,
          urgentOrders: metrics.urgentOrders,
          pendingAssignments: metrics.pendingAssignments,
          readyOrders: metrics.readyOrders,
          todayRevenue: metrics.todayRevenue,
          avgOrderValue: metrics.avgOrderValue,
          successRate: metrics.successRate,
          avgDeliveryTime: metrics.avgDeliveryTime,
          utilizationRate: metrics.utilizationRate,
          peakHour: metrics.peakHour,
          longestWait: metrics.longestWait,
          avgOrdersPerDriver: metrics.avgOrdersPerDriver
        },
        drivers: {
          total: metrics.totalDrivers,
          free: metrics.freeDrivers,
          busy: metrics.busyDrivers,
          inactive: metrics.inactiveDrivers,
          external: metrics.externalDrivers,
          active: metrics.activeDrivers,
          utilization: metrics.utilizationRate
        },
        tasks: {
          nonaccepter: tasks.nonaccepter.length,
          unassigned: tasks.unassigned.length,
          assigned: tasks.assigned.length,
          prete: tasks.prete.length,
          completed: tasks.completed.length
        },
        dateRange: {
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString()
        }
      };

      // Convert to JSON and create downloadable file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      // Create download link
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dispatch-report-${new Date().toISOString().split('T')[0]}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      // Show success message
      alert('Données exportées avec succès !');
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Échec de l\'exportation. Veuillez réessayer.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dispatch-dashboard-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="dashboard-header">
          <h3>
            <i className="fas fa-tachometer-alt"></i>
            Vue d'ensemble de la livraison
          </h3>
          <div className="dashboard-controls">
            <button 
              className={`auto-refresh-toggle ${autoRefresh ? 'active' : ''}`}
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? 'Actualisation automatique activée' : 'Actualisation automatique désactivée'}
            >
              <i className={`fas ${autoRefresh ? 'fa-sync-alt' : 'fa-pause'}`}></i>
            </button>
            <button 
              className="refresh-button"
              onClick={onRefresh}
              title="Actualiser maintenant"
            >
              <i className="fas fa-sync"></i>
            </button>
            <button className="close-button" onClick={onClose} title="Fermer le tableau de bord">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Key Metrics Row */}
          <div className="metrics-row">
            {metrics.urgentOrders > 0 && (
              <div className="metric-card urgent">
                <div className="metric-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{metrics.urgentOrders}</div>
                  <div className="metric-label">Commandes urgentes</div>
                  <div className="metric-subtitle">{metrics.longestWait > 0 ? `Plus longue: ${metrics.longestWait}m` : 'Tout à jour'}</div>
                </div>
                <div className="pulse-indicator"></div>
              </div>
            )}

            {metrics.pendingAssignments > 0 && (
              <div className="metric-card warning">
                <div className="metric-icon">
                  <i className="fas fa-user-clock"></i>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{metrics.pendingAssignments}</div>
                  <div className="metric-label">Assignations en attente</div>
                  <div className="metric-subtitle">{metrics.freeDrivers} livreurs disponibles</div>
                </div>
              </div>
            )}

            {metrics.readyOrders > 0 && (
              <div className="metric-card success">
                <div className="metric-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{metrics.readyOrders}</div>
                  <div className="metric-label">Prêtes à récupérer</div>
                  <div className="metric-subtitle">Prêtes à expédier</div>
                </div>
              </div>
            )}

            {metrics.completedToday > 0 && (
              <div className="metric-card info">
                <div className="metric-icon">
                  <i className="fas fa-truck"></i>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{metrics.completedToday}</div>
                  <div className="metric-label">Terminées aujourd'hui</div>
                  <div className="metric-subtitle">{metrics.successRate}% taux de réussite</div>
                </div>
              </div>
            )}

            {metrics.todayRevenue > 0 && (
              <div className="metric-card revenue">
                <div className="metric-icon">
                  <i className="fas fa-money-bill-wave"></i>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{metrics.todayRevenue.toFixed(0)}</div>
                  <div className="metric-label">Revenus d'aujourd'hui</div>
                  <div className="metric-subtitle">{metrics.avgOrderValue} MAD moyenne</div>
                </div>
              </div>
            )}

            {metrics.avgDeliveryTime > 0 && (
              <div className="metric-card performance">
                <div className="metric-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div className="metric-info">
                  <div className="metric-value">{metrics.avgDeliveryTime}m</div>
                  <div className="metric-label">Temps de livraison moyen</div>
                  <div className="metric-subtitle">Pic: {metrics.peakHour}</div>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Stats Section */}
          <div className="enhanced-stats-grid">
            <div className="stats-card driver-stats">
            <h4>
              <i className="fas fa-users"></i>
              Aperçu des livreurs
            </h4>
              <div className="stats-content">
                <div className="stat-row">
                  <div className="status-item free">
                    <div className="status-dot" style={{ backgroundColor: getDriverStatusColor('FREE') }}></div>
                    <span>Libre: {metrics.freeDrivers}</span>
                  </div>
                  <div className="status-item busy">
                    <div className="status-dot" style={{ backgroundColor: getDriverStatusColor('BUSY') }}></div>
                    <span>Occupé: {metrics.busyDrivers}</span>
                  </div>
                </div>
                <div className="stat-row">
                  <div className="status-item inactive">
                    <div className="status-dot" style={{ backgroundColor: getDriverStatusColor('INACTIVE') }}></div>
                    <span>Inactif: {metrics.inactiveDrivers}</span>
                  </div>
                  <div className="status-item external">
                    <div className="status-dot" style={{ backgroundColor: getDriverStatusColor('EXTERNE') }}></div>
                    <span>Externe: {metrics.externalDrivers}</span>
                  </div>
                </div>
                <div className="stat-highlight">
                  <div className="highlight-value">{metrics.utilizationRate}%</div>
                  <div className="highlight-label">Taux d'utilisation</div>
                </div>
              </div>
            </div>

            <div className="stats-card order-stats">
            <h4>
              <i className="fas fa-shopping-cart"></i>
              Statistiques des commandes
            </h4>
              <div className="stats-content">
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-value">{metrics.todayTasksCount}</span>
                    <span className="stat-label">Commandes d'aujourd'hui</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{metrics.failedToday}</span>
                    <span className="stat-label">Échouées</span>
                  </div>
                </div>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-value">{metrics.avgOrdersPerDriver}</span>
                    <span className="stat-label">Moyenne par livreur</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{metrics.totalOrders}</span>
                    <span className="stat-label">Total des commandes</span>
                  </div>
                </div>
                <div className="stat-highlight">
                  <div className="highlight-value">{metrics.successRate}%</div>
                  <div className="highlight-label">Taux de réussite</div>
                </div>
              </div>
            </div>

            <div className="stats-card performance-stats">
              <h4>
                <i className="fas fa-tachometer-alt"></i>
                Performance
              </h4>
              <div className="stats-content">
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-value">{metrics.avgDeliveryTime}m</span>
                    <span className="stat-label">Livraison moyenne</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{metrics.peakHour}</span>
                    <span className="stat-label">Heure de pointe</span>
                  </div>
                </div>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-value">{metrics.longestWait}m</span>
                    <span className="stat-label">Attente la plus longue</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{metrics.activeDrivers}</span>
                    <span className="stat-label">Livreurs actifs</span>
                  </div>
                </div>
                <div className="stat-highlight">
                  <div className="highlight-value">{metrics.avgOrderValue}</div>
                  <div className="highlight-label">Valeur moyenne des commandes (MAD)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h4>Actions rapides</h4>
            <div className="action-buttons">
              <button 
                className="action-btn view-urgent" 
                title="Voir les commandes urgentes"
                onClick={() => handleViewUrgentOrders()}
              >
                <i className="fas fa-fire"></i>
                <span>Commandes urgentes</span>
              </button>
              <button 
                className="action-btn driver-stats" 
                title="Performance des livreurs"
                onClick={() => setShowPerformanceMetrics(true)}
              >
                <i className="fas fa-chart-line"></i>
                <span>Stats livreurs</span>
              </button>
              <button 
                className="action-btn export-data" 
                title="Exporter les données"
                onClick={() => handleExportData()}
              >
                <i className="fas fa-download"></i>
                <span>Exporter les données</span>
              </button>
              <button className="action-btn refresh-data" title="Actualiser toutes les données">
                <i className="fas fa-sync-alt"></i>
                <span>Actualiser</span>
              </button>
            </div>
          </div>

          {/* Date Range Info */}
          <div className="date-range-info">
            <i className="fas fa-calendar-alt"></i>
            <span>
              Affichage des données du {dateRange.startDate.toLocaleDateString('fr-FR')} au {dateRange.endDate.toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        {/* Performance Metrics Modal */}
        <PerformanceMetrics
          tasks={tasks}
          drivers={drivers}
          isOpen={showPerformanceMetrics}
          onClose={() => setShowPerformanceMetrics(false)}
        />
      </div>
    </div>
  );
};

export default DispatchDashboard;
